import React from 'react'
import { Lock } from 'lucide-react'
import { Tooltip } from '@nextui-org/react'
import { SkillEnum, SkillState } from '../../types'
import SKILLS from '../../constants/skills'
import { ProgressBar } from './ProgressBar'
import SkillShortcut from './SkillShortcut'
import { formatCurrency } from '@/utils'
import { SKILL_LEVEL_CAP } from '@/constants/game'

interface IndividualSkillCardProps {
  skillKey: SkillEnum
  reachedLevel: number
  requiredLevel: number
  gameSkill: SkillState
  showDetails?: boolean
  showLevel?: boolean
  isGoatLevel?: boolean
}

export const IndividualSkillCard: React.FC<IndividualSkillCardProps> = ({
  skillKey,
  reachedLevel,
  requiredLevel,
  gameSkill,
  showDetails = false,
  showLevel = true,
  isGoatLevel = false
}) => {
  const skill = SKILLS[skillKey]
  const isUnlocked = reachedLevel >= requiredLevel
  const skillCap = isGoatLevel ? SKILL_LEVEL_CAP + 1 : SKILL_LEVEL_CAP
  const reqLevel = requiredLevel > skillCap ? skillCap : requiredLevel
  const nextLevelPoints = skill.levels[`${skillKey}${reqLevel}` as keyof typeof skill.levels].points

  // If skill level is 0, show locked state
  if (reachedLevel === 0) {
    return (
      <div className="bg-blue-800 bg-opacity-50 rounded-lg p-3">
        <div className="flex items-center">
          <div className="mr-3 p-2">
            <Tooltip content="To unlock new skills, complete main quests of the story">
              <div className="w-8 h-8 bg-gray-700 rounded flex items-center justify-center">
                <Lock className="w-4 h-4 text-gray-400" />
              </div>
            </Tooltip>
          </div>
          <div className="flex-grow">
            <Tooltip content="To unlock new skills, complete main quests of the story">
              <h5 className="text-sm font-semibold text-gray-400 capitalize">Locked</h5>
            </Tooltip>
            {showLevel && (
              <div className="flex items-center">
                <div className="w-12 mr-1">
                  <ProgressBar progress={0} total={100} />
                </div>
                <span className="text-xs text-gray-400">0/{formatCurrency(nextLevelPoints)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const getSkillColor = (skillType: string) => {
    switch (skillType) {
      case 'base':
        return 'bg-yellow-700'
      case 'knowledge':
        return 'bg-purple-700'
      case 'maneuver':
        return 'bg-green-700'
      default:
        return 'bg-gray-700'
    }
  }

  return (
    <div className="bg-blue-800 bg-opacity-50 rounded-lg p-3">
      <div className="flex items-center">
        <div className="mr-3 p-2">
          <SkillShortcut skill={skillKey} level={reachedLevel} />
        </div>
        <div className="flex-grow">
          <div className="flex items-center gap-2 flex-wrap">
            <h5 className={`font-semibold text-white ${showDetails ? 'text-lg' : 'text-sm'} capitalize`}>
              {skill.name}
            </h5>
            {showDetails && (
              <span className={`${getSkillColor(skill.type)} px-2 py-0.5 rounded text-xs text-white font-medium`}>
                {skill.type}
              </span>
            )}
          </div>
          {showLevel && (
            <div className="flex items-center">
              <div className="w-12 mr-1">
                <ProgressBar progress={isUnlocked ? 100 : (gameSkill.points / nextLevelPoints) * 100} total={100} />
              </div>
              <span className={`text-xs font-bold ${isUnlocked ? 'text-green-400' : 'text-yellow-300'}`}>
                Level {reachedLevel}/{reqLevel}
              </span>
              <span className="text-xs text-gray-200 ml-2">
                {formatCurrency(gameSkill.points)}/{formatCurrency(nextLevelPoints)}
              </span>
            </div>
          )}
        </div>
      </div>

      {showDetails && (
        <div className="mt-4 space-y-4">
          <p className="text-white text-sm">{skill.description}</p>

          <div className="space-y-2">
            {Array.from({ length: reachedLevel + 1 }).map((_, index) => {
              const levelKey = `${skillKey}${index}` as keyof typeof skill.levels
              const levelData = skill.levels[levelKey]
              return (
                <div key={index} className="flex items-center space-x-2">
                  <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">L{index}</span>
                  <span className="text-white text-sm">{levelData.name}</span>
                  <span className="text-gray-200 text-xs">{levelData.points} points</span>
                </div>
              )
            })}

            {reachedLevel < 5 && (
              <div className="flex items-center space-x-2 opacity-75">
                <span className="bg-gray-600 text-white text-xs px-2 py-1 rounded-full">L{reachedLevel + 1}</span>
                <span className="text-white text-sm">Locked</span>
                <span className="text-gray-200 text-xs">
                  {formatCurrency(skill.levels[`${skillKey}${reachedLevel + 1}` as keyof typeof skill.levels].points)}{' '}
                  points
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
